// ============================================
// Admin Portal — App.tsx
// Unified login → Role-based auto-redirect
// Layout shell with sidebar + header
// ============================================
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@school/shared'; // Initialize i18n (side-effect)
import './tokens.css'; // Portal-specific theme bridge tokens
import './pages/modules/Modules.css'; // Global module styles (dark theme)
import { AdminLayout } from './layouts/AdminLayout';

// Auth pages (no layout)
import Login from './pages/Login';

// SaaS Registration
const PlatformAdmin = React.lazy(() => import('./pages/platform/PlatformAdmin'));

// Public CMS Website
const PublicRoot = React.lazy(() => import('./pages/public/PublicRoot'));
const PublicHome = React.lazy(() => import('./pages/public/PublicHome'));
const PublicPage = React.lazy(() => import('./pages/public/PublicPage'));
const PublicRead = React.lazy(() => import('./pages/public/PublicRead'));
const PublicContact = React.lazy(() => import('./pages/public/PublicContact'));
const PublicAdmission = React.lazy(() => import('./pages/public/PublicAdmission'));
const PublicEvents = React.lazy(() => import('./pages/public/PublicEvents'));
const PublicNotices = React.lazy(() => import('./pages/public/PublicNotices'));
const PublicGallery = React.lazy(() => import('./pages/public/PublicGallery'));
const RootGateway = React.lazy(() => import('./pages/public/RootGateway'));

// Dashboard
const Dashboard = React.lazy(() => import('./pages/Dashboard'));

// Placeholder page for modules not yet built
const ComingSoon = React.lazy(() => import('./pages/ComingSoon'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));

// Settings
const GeneralSettings = React.lazy(() => import('./pages/settings/GeneralSettings'));
const SessionSettings = React.lazy(() => import('./pages/settings/SessionSettings'));
const NotificationSettings = React.lazy(() => import('./pages/settings/NotificationSettings'));
const SmsSettings = React.lazy(() => import('./pages/settings/SmsSettings'));
const EmailSettings = React.lazy(() => import('./pages/settings/EmailSettings'));
const PaymentSettings = React.lazy(() => import('./pages/settings/PaymentSettings'));
const PrintSettings = React.lazy(() => import('./pages/settings/PrintSettings'));
const RolesPermissions = React.lazy(() => import('./pages/settings/RolesPermissions'));
const FrontCmsSettings = React.lazy(() => import('./pages/settings/FrontCmsSettings'));
const ModuleSettings = React.lazy(() => import('./pages/settings/ModuleSettings'));
const LanguageSettings = React.lazy(() => import('./pages/settings/LanguageSettings'));
const UserSettings = React.lazy(() => import('./pages/settings/UserSettings'));
const BackupSettings = React.lazy(() => import('./pages/settings/BackupSettings'));
const CustomFieldSettings = React.lazy(() => import('./pages/settings/CustomFieldSettings'));
const CaptchaSettings = React.lazy(() => import('./pages/settings/CaptchaSettings'));
const SystemFieldSettings = React.lazy(() => import('./pages/settings/SystemFieldSettings'));
const ProfileUpdateSettings = React.lazy(() => import('./pages/settings/ProfileUpdateSettings'));
const FileTypeSettings = React.lazy(() => import('./pages/settings/FileTypeSettings'));
const ActivityLogs = React.lazy(() => import('./pages/settings/ActivityLogs'));
const UserLog = React.lazy(() => import('./pages/reports/UserLog'));

// Analytics
const AiAnalyticsPage = React.lazy(() => import('./pages/AiAnalyticsPage'));
const AiTutorDashboard = React.lazy(() => import('./pages/ai-tutor/AiTutorDashboard'));

// CMS Module Pages
const CmsMedia = React.lazy(() => import('./pages/cms/CmsMedia'));
const CmsEvents = React.lazy(() => import('./pages/cms/CmsEvents'));
const CmsNotice = React.lazy(() => import('./pages/cms/CmsNotice'));
const CmsBanner = React.lazy(() => import('./pages/cms/CmsBanner'));
const CmsGallery = React.lazy(() => import('./pages/cms/CmsGallery'));
const CmsPages = React.lazy(() => import('./pages/cms/CmsPages'));
const CmsMenus = React.lazy(() => import('./pages/cms/CmsMenus'));

// Alumni Module
const ManageAlumni = React.lazy(() => import('./pages/alumni/ManageAlumni'));
const AlumniEvents = React.lazy(() => import('./pages/alumni/AlumniEvents'));

// Certificate & ID Cards Module
const CertTemplates = React.lazy(() => import('./pages/certificates/CertTemplates'));
const StudentIdCards = React.lazy(() => import('./pages/certificates/StudentIdCards'));
const StaffIdCards = React.lazy(() => import('./pages/certificates/StaffIdCards'));
const VerifyCertificate = React.lazy(() => import('./pages/certificates/VerifyCertificate'));

// Inventory Module
const InventoryDashboard = React.lazy(() => import('./pages/inventory/InventoryDashboard'));
const ItemCategories = React.lazy(() => import('./pages/inventory/ItemCategories'));
const ItemStores = React.lazy(() => import('./pages/inventory/ItemStores'));
const ItemLocations = React.lazy(() => import('./pages/inventory/ItemLocations'));
const InventoryReports = React.lazy(() => import('./pages/inventory/InventoryReports'));
const ItemSuppliers = React.lazy(() => import('./pages/inventory/ItemSuppliers'));
const ManageItems = React.lazy(() => import('./pages/inventory/ManageItems'));
const ItemStock = React.lazy(() => import('./pages/inventory/ItemStock'));
const IssueItems = React.lazy(() => import('./pages/inventory/IssueItems'));
const InventorySettings = React.lazy(() => import('./pages/inventory/InventorySettings'));
const MyInventory = React.lazy(() => import('./pages/inventory/MyInventory'));

// Library Module
const LibraryDashboard = React.lazy(() => import('./pages/library/LibraryDashboard'));
const AddBook = React.lazy(() => import('./pages/library/AddBook'));
const BookList = React.lazy(() => import('./pages/library/BookList'));
const IssueReturn = React.lazy(() => import('./pages/library/IssueReturn'));
const AddMember = React.lazy(() => import('./pages/library/AddMember'));
const BookReports = React.lazy(() => import('./pages/library/BookReports'));
const LibrarySettings = React.lazy(() => import('./pages/library/LibrarySettings'));

// Download Center Module
const DownloadDashboard = React.lazy(() => import('./pages/downloads/DownloadDashboard'));
const UploadContent = React.lazy(() => import('./pages/downloads/UploadContent'));
const ContentByType = React.lazy(() => import('./pages/downloads/ContentByType'));

// Communication Module
const CommDashboard = React.lazy(() => import('./pages/communication/CommDashboard'));
const NoticeBoard = React.lazy(() => import('./pages/communication/NoticeBoard'));
const SendEmail = React.lazy(() => import('./pages/communication/SendEmail'));
const SendSms = React.lazy(() => import('./pages/communication/SendSms'));
const EmailSmsLog = React.lazy(() => import('./pages/communication/EmailSmsLog'));
const CommNotificationSettings = React.lazy(() => import('./pages/communication/CommNotificationSettings'));

// HR Module — separate pages per sidebar menu item
const StaffDirectory = React.lazy(() => import('./pages/hr/StaffDirectory'));
const StaffAttendance = React.lazy(() => import('./pages/hr/StaffAttendance'));
const StudyHours = React.lazy(() => import('./pages/hr/StudyHours'));
const StudyHoursRoster = React.lazy(() => import('./pages/hr/StudyHoursRoster'));
const Payroll = React.lazy(() => import('./pages/hr/Payroll'));
const ApproveLeaveRequest = React.lazy(() => import('./pages/hr/ApproveLeaveRequest'));
const ApplyLeave = React.lazy(() => import('./pages/hr/ApplyLeave'));
const LeaveTypes = React.lazy(() => import('./pages/hr/LeaveTypes'));
const TeachersRating = React.lazy(() => import('./pages/hr/TeachersRating'));
const Department = React.lazy(() => import('./pages/hr/Department'));
const Designation = React.lazy(() => import('./pages/hr/Designation'));
const DisabledStaff = React.lazy(() => import('./pages/hr/DisabledStaff'));
const StaffForm = React.lazy(() => import('./pages/hr/StaffForm'));
const StaffProfile = React.lazy(() => import('./pages/hr/StaffProfile'));
const PayrollSettings = React.lazy(() => import('./pages/hr/PayrollSettings'));
const StatutoryReport = React.lazy(() => import('./pages/hr/StatutoryReport'));
const AttendanceAdvanced = React.lazy(() => import('./pages/hr/AttendanceAdvanced'));
const Notifications = React.lazy(() => import('./pages/hr/Notifications'));
const BiometricManagement = React.lazy(() => import('./pages/hr/BiometricManagement'));
const OrgTree = React.lazy(() => import('./pages/hr/OrgTree'));

// Homework Module
const HomeworkPage = React.lazy(() => import('./pages/homework/HomeworkPage'));
const HomeworkSettings = React.lazy(() => import('./pages/homework/HomeworkSettings'));

// Lesson Plan Module
const LessonPage = React.lazy(() => import('./pages/lesson-plan/LessonPage'));
const TopicPage = React.lazy(() => import('./pages/lesson-plan/TopicPage'));
const SyllabusStatusPage = React.lazy(() => import('./pages/lesson-plan/SyllabusStatusPage'));
const ManageLessonPlanPage = React.lazy(() => import('./pages/lesson-plan/ManageLessonPlanPage'));
const AnalyticsDashboard = React.lazy(() => import('./pages/lesson-plan/AnalyticsDashboard'));

// Attendance Module
const StudentAttendance = React.lazy(() => import('./pages/attendance/StudentAttendance'));
const AttendanceByDate = React.lazy(() => import('./pages/attendance/AttendanceByDate'));
const ClassAttendanceReport = React.lazy(() => import('./pages/attendance/ClassAttendanceReport'));
const QrAttendance = React.lazy(() => import('./pages/attendance/QrAttendance'));
const ApproveLeave = React.lazy(() => import('./pages/attendance/ApproveLeave'));
const AttendanceAnalytics = React.lazy(() => import('./pages/attendance/AttendanceAnalytics'));
const BiometricLogs = React.lazy(() => import('./pages/attendance/BiometricLogs'));

// Examinations Module
const ExamGroupList = React.lazy(() => import('./pages/examinations/ExamGroupList'));
const ExamGroupDetail = React.lazy(() => import('./pages/examinations/ExamGroupDetail'));
const ExamSchedule = React.lazy(() => import('./pages/examinations/ExamSchedule'));
const MarkEntry = React.lazy(() => import('./pages/examinations/MarkEntry'));
const ExamResult = React.lazy(() => import('./pages/examinations/ExamResult'));
const GradeManager = React.lazy(() => import('./pages/examinations/GradeManager'));
const AdmitCardDesigner = React.lazy(() => import('./pages/examinations/AdmitCardDesigner'));
const PrintAdmitCard = React.lazy(() => import('./pages/examinations/PrintAdmitCard'));
const MarksheetDesigner = React.lazy(() => import('./pages/examinations/MarksheetDesigner'));
const PrintMarksheet = React.lazy(() => import('./pages/examinations/PrintMarksheet'));
const BulkProgressReport = React.lazy(() => import('./pages/examinations/BulkProgressReport'));

// Online Examinations Module
const OnlineExamList = React.lazy(() => import('./pages/online-exam/OnlineExamList'));
const OnlineExamSettings = React.lazy(() => import('./pages/online-exam/OnlineExamSettings'));
const ExamManage = React.lazy(() => import('./pages/online-exam/ExamManage'));
const PracticeAnalytics = React.lazy(() => import('./pages/online-exam/PracticeAnalytics'));
const QuestionBankPage = React.lazy(() => import('./pages/online-exam/QuestionBankPage'));
const QuestionBankSettings = React.lazy(() => import('./pages/online-exam/questionBank/QuestionBankSettings'));
const QuestionUploadStats = React.lazy(() => import('./pages/online-exam/questionBank/QuestionUploadStats'));
const QuestionBulkUploadPage = React.lazy(() => import('./pages/online-exam/QuestionBulkUploadModal'));
const StudentErrorsPage = React.lazy(() => import('./pages/online-exam/StudentErrorsPage'));
const AdminStudentResultPage = React.lazy(() => import('./pages/online-exam/AdminStudentResultPage'));

// Income Module
const IncomePage = React.lazy(() => import('./pages/income/IncomePage'));
const IncomeSearchPage = React.lazy(() => import('./pages/income/IncomeSearchPage'));
const IncomeHeadPage = React.lazy(() => import('./pages/income/IncomeHeadPage'));

// Expense Module
const ExpensePage = React.lazy(() => import('./pages/expense/ExpensePage'));
const ExpenseSearchPage = React.lazy(() => import('./pages/expense/ExpenseSearchPage'));
const ExpenseHeadPage = React.lazy(() => import('./pages/expense/ExpenseHeadPage'));

// Academics Module
const ClassesPage = React.lazy(() => import('./pages/academics/ClassesPage'));
const SectionsPage = React.lazy(() => import('./pages/academics/SectionsPage'));
const SubjectsPage = React.lazy(() => import('./pages/academics/SubjectsPage'));
const SubjectGroupPage = React.lazy(() => import('./pages/academics/SubjectGroupPage'));
const GradesPage = React.lazy(() => import('./pages/academics/GradesPage'));
const ClassTimetable = React.lazy(() => import('./pages/academics/ClassTimetable'));
const TeacherTimetable = React.lazy(() => import('./pages/academics/TeacherTimetable'));
const AssignClassTeacher = React.lazy(() => import('./pages/academics/AssignClassTeacher'));
const AssignSubjectTeacher = React.lazy(() => import('./pages/academics/AssignSubjectTeacher'));
const PromoteStudents = React.lazy(() => import('./pages/academics/PromoteStudents'));

// Fees Collection Module
const FeeSummaryPage = React.lazy(() => import('./pages/fees/FeeSummaryPage'));
const FeeCollectPage = React.lazy(() => import('./pages/fees/FeeCollectPage'));
const FeeSearchPage = React.lazy(() => import('./pages/fees/FeeSearchPage'));
const FeeDuePage = React.lazy(() => import('./pages/fees/FeeDuePage'));
const FeeGroupPage = React.lazy(() => import('./pages/fees/FeeGroupPage'));
const FeeTypePage = React.lazy(() => import('./pages/fees/FeeTypePage'));
const FeeMasterPage = React.lazy(() => import('./pages/fees/FeeMasterPage'));
const FeeDiscountPage = React.lazy(() => import('./pages/fees/FeeDiscountPage'));
const FeesForwardPage = React.lazy(() => import('./pages/fees/FeesForwardPage'));
const FeeReminderPage = React.lazy(() => import('./pages/fees/FeeReminderPage'));
const FeeAssignPage = React.lazy(() => import('./pages/fees/FeeAssignPage'));

// Front Office Module
const FrontOfficeDashboard = React.lazy(() => import('./pages/front-office/FrontOfficeDashboard'));
const EnquiryPage = React.lazy(() => import('./pages/front-office/EnquiryPage'));
const VisitorPage = React.lazy(() => import('./pages/front-office/VisitorPage'));
const ComplaintPage = React.lazy(() => import('./pages/front-office/ComplaintPage'));
const DispatchPage = React.lazy(() => import('./pages/front-office/DispatchPage'));
const PhoneCallPage = React.lazy(() => import('./pages/front-office/PhoneCallPage'));
const FOSetupPage = React.lazy(() => import('./pages/front-office/SetupPage'));

// Student Information Module
const StudentSearchPage = React.lazy(() => import('./pages/students/StudentSearchPage'));
const StudentAdmissionPage = React.lazy(() => import('./pages/students/StudentAdmissionPage'));
const StudentProfilePage = React.lazy(() => import('./pages/students/StudentProfilePage'));
const StudentEditPage = React.lazy(() => import('./pages/students/StudentEditPage'));
const DisabledStudentsPage = React.lazy(() => import('./pages/students/DisabledStudentsPage'));
const DuplicateStudentsPage = React.lazy(() => import('./pages/students/DuplicateStudentsPage'));
const MultiClassPage = React.lazy(() => import('./pages/students/MultiClassPage'));
const BulkDeletePage = React.lazy(() => import('./pages/students/BulkDeletePage'));
const StudentCategoriesPage = React.lazy(() => import('./pages/students/StudentCategoriesPage'));
const StudentHousePage = React.lazy(() => import('./pages/students/StudentHousePage'));
const DisableReasonPage = React.lazy(() => import('./pages/students/DisableReasonPage'));
const OnlineAdmissionPage = React.lazy(() => import('./pages/students/OnlineAdmissionPage'));

// Hostel Management Module
const HostelModule = React.lazy(() => import('./pages/hostel/HostelModule'));

// Gate Pass Module
const GatePassModule = React.lazy(() => import('./pages/gate-pass/GatePassModule'));

// Campus Network Module (OEL-185)
const CampusNetworkModule = React.lazy(() => import('./pages/campus-network/CampusNetworkModule'));

// Student Counselling Module
const StudentCounsellingModule = React.lazy(() => import('./pages/student-counselling/StudentCounsellingModule'));

// Student Complaints Module
const ComplaintsModule = React.lazy(() => import('./pages/complaints/ComplaintsModule'));

// Calendar Module
const CalendarPage = React.lazy(() => import('./pages/calendar/CalendarPage'));

// Notification Module (Notice Board + Settings)
const NotificationModPage = React.lazy(() => import('./pages/notification/NotificationPage'));

// Chat Module
const ChatPageComp = React.lazy(() => import('./pages/chat/ChatPage'));
const ChatSettingsComp = React.lazy(() => import('./pages/chat/ChatSettings'));

// Transport Management Module
const TransportDashboard = React.lazy(() => import('./pages/transport/TransportDashboard'));
const TRouteList = React.lazy(() => import('./pages/transport/RouteList'));
const TVehicleList = React.lazy(() => import('./pages/transport/VehicleList'));
const TRouteStops = React.lazy(() => import('./pages/transport/RouteStops'));
const TVehicleRoutes = React.lazy(() => import('./pages/transport/VehicleRoutes'));
const TStudentStops = React.lazy(() => import('./pages/transport/StudentStops'));
const TTripList = React.lazy(() => import('./pages/transport/TripList'));
const TDriverTrip = React.lazy(() => import('./pages/transport/DriverTrip'));
const TNotifications = React.lazy(() => import('./pages/transport/TransportNotifications'));
const TVendorList = React.lazy(() => import('./pages/transport/VendorList'));
const TFleetManagement = React.lazy(() => import('./pages/transport/FleetManagement'));
const LiveBusPage = React.lazy(() => import('./pages/transport/LiveBusPage'));

// Reports
const SmsReports = React.lazy(() => import('./pages/reports/SmsReports'));
const AcademicReports = React.lazy(() => import('./pages/reports/academic/AcademicReports')); // OEL-193
const StudyHoursReport = React.lazy(() => import('./pages/reports/studyhours/StudyHoursReport')); // OEL-196

// Auth guard
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('auth_token');
  if (!token) return <Navigate to="/login" replace />;

  // If student/parent logged in, redirect to user portal
  const userStr = localStorage.getItem('auth_user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.role === 'student' || user.role === 'parent') {
        window.location.href = '/portal/dashboard';
        return null;
      }
    } catch {}
  }
  // Also check auth_role as fallback
  const role = localStorage.getItem('auth_role');
  if (role === 'student' || role === 'parent') {
    window.location.href = '/portal/dashboard';
    return null;
  }

  return <>{children}</>;
}

// Loading fallback
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-muted, #94a3b8)' }}>
        <div className="ui-spinner ui-spinner-lg" style={{ margin: '0 auto 1rem', border: '3px solid var(--surface-border, rgba(255,255,255,0.1))', borderTopColor: 'var(--color-primary-400, #818cf8)', borderRadius: '50%', width: 36, height: 36, animation: 'ui-spin 0.6s linear infinite' }} />
        <p>Loading module...</p>
      </div>
    </div>
  );
}

// Profile redirect — sends /profile to the logged-in user's staff profile page
function ProfileRedirect() {
  const userStr = localStorage.getItem('auth_user');
  let userId = '';
  try { const u = JSON.parse(userStr || '{}'); userId = u.id; } catch {}
  if (userId) return <Navigate to={`/hr/staff/profile/${userId}`} replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public: Unified Login */}
        <Route path="/login" element={<Login />} />
        {/* Public: SaaS Registration */}
        {/* Registering a college creates a database, so it is a platform-admin
            action now: the old public address forwards to the platform login. */}
        <Route path="/register" element={<Navigate to="/platform/register" replace />} />
        {/* Platform Admin */}
        <Route path="/platform/*" element={<Suspense fallback={<PageLoader />}><PlatformAdmin /></Suspense>} />
        {/* Public: Certificate Verification */}
        <Route path="/verify" element={<Suspense fallback={<div>Loading...</div>}><VerifyCertificate /></Suspense>} />
        <Route path="/verify/:serial" element={<Suspense fallback={<div>Loading...</div>}><VerifyCertificate /></Suspense>} />
        {/* Public: Live Bus Tracking (parent share link — no auth) */}
        <Route path="/livebus" element={<Suspense fallback={<PageLoader />}><LiveBusPage /></Suspense>} />

        {/* Public: Front CMS Website at root / */}
        <Route path="/" element={<Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'var(--surface-bg, #0a0e1a)',color:'var(--text-muted, #94a3b8)'}}><p>Loading...</p></div>}><PublicRoot /></Suspense>}>
          <Route index element={<Suspense fallback={<div/>}><PublicHome /></Suspense>} />
          <Route path="page/:slug" element={<Suspense fallback={<div/>}><PublicPage /></Suspense>} />
          <Route path="read/:slug" element={<Suspense fallback={<div/>}><PublicRead /></Suspense>} />
          <Route path="contact" element={<Suspense fallback={<div/>}><PublicContact /></Suspense>} />
          <Route path="admission" element={<Suspense fallback={<div/>}><PublicAdmission /></Suspense>} />
          <Route path="events" element={<Suspense fallback={<div/>}><PublicEvents /></Suspense>} />
          <Route path="notices" element={<Suspense fallback={<div/>}><PublicNotices /></Suspense>} />
          <Route path="gallery" element={<Suspense fallback={<div/>}><PublicGallery /></Suspense>} />
        </Route>

        {/* Protected: Admin Shell (pathless layout — children match /dashboard, /settings/*, etc.) */}
        <Route element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />

          {/* Settings module */}
          <Route path="settings/general" element={<Suspense fallback={<PageLoader />}><GeneralSettings /></Suspense>} />
          <Route path="settings/session" element={<Suspense fallback={<PageLoader />}><SessionSettings /></Suspense>} />
          <Route path="settings/notification" element={<Suspense fallback={<PageLoader />}><NotificationSettings /></Suspense>} />
          <Route path="settings/sms" element={<Suspense fallback={<PageLoader />}><SmsSettings /></Suspense>} />
          <Route path="settings/email" element={<Suspense fallback={<PageLoader />}><EmailSettings /></Suspense>} />
          <Route path="settings/payment" element={<Suspense fallback={<PageLoader />}><PaymentSettings /></Suspense>} />
          <Route path="settings/print" element={<Suspense fallback={<PageLoader />}><PrintSettings /></Suspense>} />
          <Route path="settings/roles" element={<Suspense fallback={<PageLoader />}><RolesPermissions /></Suspense>} />
          <Route path="settings/cms" element={<Suspense fallback={<PageLoader />}><FrontCmsSettings /></Suspense>} />
          <Route path="settings/modules" element={<Suspense fallback={<PageLoader />}><ModuleSettings /></Suspense>} />
          <Route path="settings/language" element={<Suspense fallback={<PageLoader />}><LanguageSettings /></Suspense>} />
          <Route path="settings/users" element={<Suspense fallback={<PageLoader />}><UserSettings /></Suspense>} />
          <Route path="settings/backup" element={<Suspense fallback={<PageLoader />}><BackupSettings /></Suspense>} />
          <Route path="settings/custom-fields" element={<Suspense fallback={<PageLoader />}><CustomFieldSettings /></Suspense>} />
          <Route path="settings/captcha" element={<Suspense fallback={<PageLoader />}><CaptchaSettings /></Suspense>} />
          <Route path="settings/system-fields" element={<Suspense fallback={<PageLoader />}><SystemFieldSettings /></Suspense>} />
          <Route path="settings/profile-update" element={<Suspense fallback={<PageLoader />}><ProfileUpdateSettings /></Suspense>} />
          <Route path="settings/file-types" element={<Suspense fallback={<PageLoader />}><FileTypeSettings /></Suspense>} />
          <Route path="settings/activity-logs" element={<Suspense fallback={<PageLoader />}><ActivityLogs /></Suspense>} />

          {/* Front CMS Module */}
          <Route path="cms/media" element={<Suspense fallback={<PageLoader />}><CmsMedia /></Suspense>} />
          <Route path="cms/events" element={<Suspense fallback={<PageLoader />}><CmsEvents /></Suspense>} />
          <Route path="cms/notice" element={<Suspense fallback={<PageLoader />}><CmsNotice /></Suspense>} />
          <Route path="cms/banner" element={<Suspense fallback={<PageLoader />}><CmsBanner /></Suspense>} />
          <Route path="cms/gallery" element={<Suspense fallback={<PageLoader />}><CmsGallery /></Suspense>} />
          <Route path="cms/pages" element={<Suspense fallback={<PageLoader />}><CmsPages /></Suspense>} />
          <Route path="cms/menus" element={<Suspense fallback={<PageLoader />}><CmsMenus /></Suspense>} />

          {/* Alumni Module */}
          <Route path="alumni/manage" element={<Suspense fallback={<PageLoader />}><ManageAlumni /></Suspense>} />
          <Route path="alumni/events" element={<Suspense fallback={<PageLoader />}><AlumniEvents /></Suspense>} />
          <Route path="alumni" element={<Navigate to="/alumni/manage" replace />} />

          {/* Certificate & ID Cards Module */}
          <Route path="certificate/templates" element={<Suspense fallback={<PageLoader />}><CertTemplates /></Suspense>} />
          <Route path="certificate/student-id" element={<Suspense fallback={<PageLoader />}><StudentIdCards /></Suspense>} />
          <Route path="certificate/staff-id" element={<Suspense fallback={<PageLoader />}><StaffIdCards /></Suspense>} />
          <Route path="certificate" element={<Navigate to="/certificate/templates" replace />} />

          {/* Inventory Module */}
          <Route path="inventory/dashboard" element={<Suspense fallback={<PageLoader />}><InventoryDashboard /></Suspense>} />
          <Route path="inventory/categories" element={<Suspense fallback={<PageLoader />}><ItemCategories /></Suspense>} />
          <Route path="inventory/stores" element={<Suspense fallback={<PageLoader />}><ItemStores /></Suspense>} />
          <Route path="inventory/locations" element={<Suspense fallback={<PageLoader />}><ItemLocations /></Suspense>} />
          <Route path="inventory/reports" element={<Suspense fallback={<PageLoader />}><InventoryReports /></Suspense>} />
          <Route path="inventory/suppliers" element={<Suspense fallback={<PageLoader />}><ItemSuppliers /></Suspense>} />
          <Route path="inventory/items" element={<Suspense fallback={<PageLoader />}><ManageItems /></Suspense>} />
          <Route path="inventory/stock" element={<Suspense fallback={<PageLoader />}><ItemStock /></Suspense>} />
          <Route path="inventory/issues" element={<Suspense fallback={<PageLoader />}><IssueItems /></Suspense>} />
          <Route path="inventory/my-items" element={<Suspense fallback={<PageLoader />}><MyInventory /></Suspense>} />
          <Route path="inventory/settings" element={<Suspense fallback={<PageLoader />}><InventorySettings /></Suspense>} />
          <Route path="inventory" element={<Navigate to="/inventory/dashboard" replace />} />

          {/* Library Module */}
          <Route path="library/dashboard" element={<Suspense fallback={<PageLoader />}><LibraryDashboard /></Suspense>} />
          <Route path="library/add-book" element={<Suspense fallback={<PageLoader />}><AddBook /></Suspense>} />
          <Route path="library/books" element={<Suspense fallback={<PageLoader />}><BookList /></Suspense>} />
          <Route path="library/issue-return" element={<Suspense fallback={<PageLoader />}><IssueReturn /></Suspense>} />
          <Route path="library/members" element={<Suspense fallback={<PageLoader />}><AddMember /></Suspense>} />
          <Route path="library/reports" element={<Suspense fallback={<PageLoader />}><BookReports /></Suspense>} />
          <Route path="library/settings" element={<Suspense fallback={<PageLoader />}><LibrarySettings /></Suspense>} />
          <Route path="library" element={<Navigate to="/library/dashboard" replace />} />

          {/* Download Center Module */}
          <Route path="downloads/dashboard" element={<Suspense fallback={<PageLoader />}><DownloadDashboard /></Suspense>} />
          <Route path="downloads/upload" element={<Suspense fallback={<PageLoader />}><UploadContent /></Suspense>} />
          <Route path="downloads/assignments" element={<Suspense fallback={<PageLoader />}><ContentByType /></Suspense>} />
          <Route path="downloads/study-material" element={<Suspense fallback={<PageLoader />}><ContentByType /></Suspense>} />
          <Route path="downloads/syllabus" element={<Suspense fallback={<PageLoader />}><ContentByType /></Suspense>} />
          <Route path="downloads/other" element={<Suspense fallback={<PageLoader />}><ContentByType /></Suspense>} />
          <Route path="downloads" element={<Navigate to="/downloads/dashboard" replace />} />

          {/* Communication Module */}
          <Route path="communication/dashboard" element={<Suspense fallback={<PageLoader />}><CommDashboard /></Suspense>} />
          <Route path="communication/notice" element={<Suspense fallback={<PageLoader />}><NoticeBoard /></Suspense>} />
          <Route path="communication/email" element={<Suspense fallback={<PageLoader />}><SendEmail /></Suspense>} />
          <Route path="communication/sms" element={<Suspense fallback={<PageLoader />}><SendSms /></Suspense>} />
          <Route path="communication/log" element={<Suspense fallback={<PageLoader />}><EmailSmsLog /></Suspense>} />
          <Route path="communication/settings" element={<Suspense fallback={<PageLoader />}><CommNotificationSettings /></Suspense>} />
          <Route path="communication" element={<Navigate to="/communication/dashboard" replace />} />

          {/* Human Resource Module — menu-based routing */}
          <Route path="hr/staff" element={<Suspense fallback={<PageLoader />}><StaffDirectory /></Suspense>} />
          <Route path="hr/staff/create" element={<Suspense fallback={<PageLoader />}><StaffForm /></Suspense>} />
          <Route path="hr/staff/edit/:id" element={<Suspense fallback={<PageLoader />}><StaffForm /></Suspense>} />
          <Route path="hr/staff/profile/:id" element={<Suspense fallback={<PageLoader />}><StaffProfile /></Suspense>} />
          <Route path="hr/attendance" element={<Suspense fallback={<PageLoader />}><StaffAttendance /></Suspense>} />
          <Route path="hr/study-hours" element={<Suspense fallback={<PageLoader />}><StudyHours /></Suspense>} />
          <Route path="hr/study-hours/roster" element={<Suspense fallback={<PageLoader />}><StudyHoursRoster /></Suspense>} />
          <Route path="hr/payroll" element={<Suspense fallback={<PageLoader />}><Payroll /></Suspense>} />
          <Route path="hr/payroll-settings" element={<Suspense fallback={<PageLoader />}><PayrollSettings /></Suspense>} />
          <Route path="hr/statutory-report" element={<Suspense fallback={<PageLoader />}><StatutoryReport /></Suspense>} />
          <Route path="hr/attendance-advanced" element={<Suspense fallback={<PageLoader />}><AttendanceAdvanced /></Suspense>} />
          <Route path="hr/notifications" element={<Suspense fallback={<PageLoader />}><Notifications /></Suspense>} />
          <Route path="hr/approve-leave" element={<Suspense fallback={<PageLoader />}><ApproveLeaveRequest /></Suspense>} />
          <Route path="hr/apply-leave" element={<Suspense fallback={<PageLoader />}><ApplyLeave /></Suspense>} />
          <Route path="hr/leave-type" element={<Suspense fallback={<PageLoader />}><LeaveTypes /></Suspense>} />
          <Route path="hr/rating" element={<Suspense fallback={<PageLoader />}><TeachersRating /></Suspense>} />
          <Route path="hr/department" element={<Suspense fallback={<PageLoader />}><Department /></Suspense>} />
          <Route path="hr/designation" element={<Suspense fallback={<PageLoader />}><Designation /></Suspense>} />
          <Route path="hr/disabled" element={<Suspense fallback={<PageLoader />}><DisabledStaff /></Suspense>} />
          <Route path="hr/biometric-management" element={<Suspense fallback={<PageLoader />}><BiometricManagement /></Suspense>} />
          <Route path="hr/organization" element={<Suspense fallback={<PageLoader />}><OrgTree /></Suspense>} />
          <Route path="hr" element={<Navigate to="/hr/staff" replace />} />

          {/* Homework Module */}
          <Route path="homework" element={<Suspense fallback={<PageLoader />}><HomeworkPage /></Suspense>} />
          <Route path="homework/settings" element={<Suspense fallback={<PageLoader />}><HomeworkSettings /></Suspense>} />

          {/* Academics Module */}
          <Route path="academics/classes" element={<Suspense fallback={<PageLoader />}><ClassesPage /></Suspense>} />
          <Route path="academics/sections" element={<Suspense fallback={<PageLoader />}><SectionsPage /></Suspense>} />
          <Route path="academics/subjects" element={<Suspense fallback={<PageLoader />}><SubjectsPage /></Suspense>} />
          <Route path="academics/subject-groups" element={<Suspense fallback={<PageLoader />}><SubjectGroupPage /></Suspense>} />
          <Route path="academics/grades" element={<Suspense fallback={<PageLoader />}><GradesPage /></Suspense>} />
          <Route path="academics/class-timetable" element={<Suspense fallback={<PageLoader />}><ClassTimetable /></Suspense>} />
          <Route path="academics/teacher-timetable" element={<Suspense fallback={<PageLoader />}><TeacherTimetable /></Suspense>} />
          <Route path="academics/assign-class-teacher" element={<Navigate to="/academics/classes" replace />} />
          <Route path="academics/assign-subject-teacher" element={<Navigate to="/academics/classes" replace />} />
          <Route path="academics/promote" element={<Suspense fallback={<PageLoader />}><PromoteStudents /></Suspense>} />
          <Route path="academics" element={<Navigate to="/academics/class-timetable" replace />} />

          {/* Lesson Plan Module */}
          <Route path="lesson-plan/manage" element={<Suspense fallback={<PageLoader />}><ManageLessonPlanPage /></Suspense>} />
          <Route path="lesson-plan/syllabus-status" element={<Suspense fallback={<PageLoader />}><SyllabusStatusPage /></Suspense>} />
          <Route path="lesson-plan/lessons" element={<Suspense fallback={<PageLoader />}><LessonPage /></Suspense>} />
          <Route path="lesson-plan/topics" element={<Suspense fallback={<PageLoader />}><TopicPage /></Suspense>} />
          <Route path="lesson-plan/analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsDashboard /></Suspense>} />
          <Route path="lesson-plan" element={<Navigate to="/lesson-plan/manage" replace />} />

          {/* Attendance Module */}
          <Route path="attendance/student" element={<Suspense fallback={<PageLoader />}><StudentAttendance /></Suspense>} />
          <Route path="attendance/by-date" element={<Suspense fallback={<PageLoader />}><AttendanceByDate /></Suspense>} />
          <Route path="attendance/class-report" element={<Suspense fallback={<PageLoader />}><ClassAttendanceReport /></Suspense>} />
          <Route path="attendance/qr" element={<Suspense fallback={<PageLoader />}><QrAttendance /></Suspense>} />
          <Route path="attendance/leave" element={<Suspense fallback={<PageLoader />}><ApproveLeave /></Suspense>} />
          <Route path="attendance/analytics" element={<Suspense fallback={<PageLoader />}><AttendanceAnalytics /></Suspense>} />
          <Route path="attendance/biometric-logs" element={<Suspense fallback={<PageLoader />}><BiometricLogs /></Suspense>} />
          <Route path="attendance" element={<Navigate to="/attendance/student" replace />} />

          {/* Examinations Module */}
          <Route path="exam/group" element={<Suspense fallback={<PageLoader />}><ExamGroupList /></Suspense>} />
          <Route path="exam/group/:id" element={<Suspense fallback={<PageLoader />}><ExamGroupDetail /></Suspense>} />
          <Route path="exam/schedule" element={<Suspense fallback={<PageLoader />}><ExamSchedule /></Suspense>} />
          <Route path="exam/marks" element={<Suspense fallback={<PageLoader />}><MarkEntry /></Suspense>} />
          <Route path="exam/result" element={<Suspense fallback={<PageLoader />}><ExamResult /></Suspense>} />
          <Route path="exam/grade" element={<Suspense fallback={<PageLoader />}><GradeManager /></Suspense>} />
          <Route path="exam/admit-card-design" element={<Suspense fallback={<PageLoader />}><AdmitCardDesigner /></Suspense>} />
          <Route path="exam/admit-card-print" element={<Suspense fallback={<PageLoader />}><PrintAdmitCard /></Suspense>} />
          <Route path="exam/marksheet-design" element={<Suspense fallback={<PageLoader />}><MarksheetDesigner /></Suspense>} />
          <Route path="exam/marksheet-print" element={<Suspense fallback={<PageLoader />}><PrintMarksheet /></Suspense>} />
          <Route path="exam/progress-report-bulk" element={<Suspense fallback={<PageLoader />}><BulkProgressReport /></Suspense>} />
          <Route path="exam" element={<Navigate to="/exam/group" replace />} />


          {/* Income Module */}
          <Route path="income/add" element={<Suspense fallback={<PageLoader />}><IncomePage /></Suspense>} />
          <Route path="income/search" element={<Suspense fallback={<PageLoader />}><IncomeSearchPage /></Suspense>} />
          <Route path="income/head" element={<Suspense fallback={<PageLoader />}><IncomeHeadPage /></Suspense>} />
          <Route path="income" element={<Navigate to="/income/add" replace />} />

          {/* Expense Module */}
          <Route path="expense/add" element={<Suspense fallback={<PageLoader />}><ExpensePage /></Suspense>} />
          <Route path="expense/search" element={<Suspense fallback={<PageLoader />}><ExpenseSearchPage /></Suspense>} />
          <Route path="expense/head" element={<Suspense fallback={<PageLoader />}><ExpenseHeadPage /></Suspense>} />
          <Route path="expense" element={<Navigate to="/expense/add" replace />} />

          {/* Fees Collection Module — paths match navigation.ts sidebar */}
          <Route path="fees" element={<Suspense fallback={<PageLoader />}><FeeSummaryPage /></Suspense>} />
          <Route path="fees/collect" element={<Suspense fallback={<PageLoader />}><FeeCollectPage /></Suspense>} />
          <Route path="fees/search" element={<Suspense fallback={<PageLoader />}><FeeSearchPage /></Suspense>} />
          <Route path="fees/due" element={<Suspense fallback={<PageLoader />}><FeeDuePage /></Suspense>} />
          <Route path="fees/master" element={<Suspense fallback={<PageLoader />}><FeeMasterPage /></Suspense>} />
          <Route path="fees/group" element={<Suspense fallback={<PageLoader />}><FeeGroupPage /></Suspense>} />
          <Route path="fees/type" element={<Suspense fallback={<PageLoader />}><FeeTypePage /></Suspense>} />
          <Route path="fees/discount" element={<Suspense fallback={<PageLoader />}><FeeDiscountPage /></Suspense>} />
          <Route path="fees/carry-forward" element={<Suspense fallback={<PageLoader />}><FeesForwardPage /></Suspense>} />
          <Route path="fees/reminder" element={<Suspense fallback={<PageLoader />}><FeeReminderPage /></Suspense>} />
          <Route path="fees/assign" element={<Suspense fallback={<PageLoader />}><FeeAssignPage /></Suspense>} />

          {/* Front Office Module — paths match navigation.ts sidebar */}
          <Route path="front-office" element={<Suspense fallback={<PageLoader />}><FrontOfficeDashboard /></Suspense>} />
          <Route path="front-office/enquiry" element={<Suspense fallback={<PageLoader />}><EnquiryPage /></Suspense>} />
          <Route path="front-office/visitors" element={<Suspense fallback={<PageLoader />}><VisitorPage /></Suspense>} />
          <Route path="front-office/complaint" element={<Suspense fallback={<PageLoader />}><ComplaintPage /></Suspense>} />
          <Route path="front-office/phone-call" element={<Suspense fallback={<PageLoader />}><PhoneCallPage /></Suspense>} />
          <Route path="front-office/postal-dispatch" element={<Suspense fallback={<PageLoader />}><DispatchPage /></Suspense>} />
          <Route path="front-office/postal-receive" element={<Suspense fallback={<PageLoader />}><DispatchPage /></Suspense>} />
          <Route path="front-office/setup" element={<Suspense fallback={<PageLoader />}><FOSetupPage /></Suspense>} />

          {/* Student Information Module — paths match navigation.ts sidebar */}
          <Route path="students/search" element={<Suspense fallback={<PageLoader />}><StudentSearchPage /></Suspense>} />
          <Route path="students/create" element={<Suspense fallback={<PageLoader />}><StudentAdmissionPage /></Suspense>} />
          <Route path="students/import" element={<Suspense fallback={<PageLoader />}><StudentAdmissionPage /></Suspense>} />
          <Route path="students/disabled" element={<Suspense fallback={<PageLoader />}><DisabledStudentsPage /></Suspense>} />
          <Route path="students/duplicates" element={<Suspense fallback={<PageLoader />}><DuplicateStudentsPage /></Suspense>} />
          <Route path="students/multi-class" element={<Suspense fallback={<PageLoader />}><MultiClassPage /></Suspense>} />
          <Route path="students/bulk-delete" element={<Suspense fallback={<PageLoader />}><BulkDeletePage /></Suspense>} />
          <Route path="students/categories" element={<Suspense fallback={<PageLoader />}><StudentCategoriesPage /></Suspense>} />
          <Route path="students/house" element={<Suspense fallback={<PageLoader />}><StudentHousePage /></Suspense>} />
          <Route path="students/disable-reason" element={<Suspense fallback={<PageLoader />}><DisableReasonPage /></Suspense>} />
          <Route path="students/online" element={<Suspense fallback={<PageLoader />}><OnlineAdmissionPage /></Suspense>} />
          <Route path="students/:id" element={<Suspense fallback={<PageLoader />}><StudentProfilePage /></Suspense>} />
          <Route path="students/:id/edit" element={<Suspense fallback={<PageLoader />}><StudentEditPage /></Suspense>} />

          {/* Online Examinations Module — paths match navigation.ts sidebar */}
          <Route path="online-exam/student-errors" element={<Suspense fallback={<PageLoader />}><StudentErrorsPage /></Suspense>} />
          <Route path="online-exam/list" element={<Suspense fallback={<PageLoader />}><OnlineExamList /></Suspense>} />
          <Route path="online-exam/:examId/result/:studentOeId" element={<Suspense fallback={<PageLoader />}><AdminStudentResultPage /></Suspense>} />
          <Route path="online-exam/:id" element={<Suspense fallback={<PageLoader />}><ExamManage /></Suspense>} />
          <Route path="online-exam/questions" element={<Suspense fallback={<PageLoader />}><QuestionBankPage /></Suspense>} />
          <Route path="online-exam/questions/bulk-upload" element={<Suspense fallback={<PageLoader />}><QuestionBulkUploadPage /></Suspense>} />
          <Route path="online-exam/questions/settings" element={<Suspense fallback={<PageLoader />}><QuestionBankSettings /></Suspense>} />
          <Route path="online-exam/question-stats" element={<Suspense fallback={<PageLoader />}><QuestionUploadStats /></Suspense>} />
          <Route path="online-exam/practice" element={<Suspense fallback={<PageLoader />}><PracticeAnalytics /></Suspense>} />
          <Route path="online-exam/settings" element={<Suspense fallback={<PageLoader />}><OnlineExamSettings /></Suspense>} />
          <Route path="online-exam" element={<Navigate to="/online-exam/list" replace />} />

          {/* Hostel Management Module */}
          <Route path="hostel" element={<Suspense fallback={<PageLoader />}><HostelModule /></Suspense>} />
          <Route path="hostel/*" element={<Suspense fallback={<PageLoader />}><HostelModule /></Suspense>} />

          {/* Campus Network Module (OEL-185) */}
          <Route path="campus-network" element={<Suspense fallback={<PageLoader />}><CampusNetworkModule /></Suspense>} />
          <Route path="campus-network/*" element={<Suspense fallback={<PageLoader />}><CampusNetworkModule /></Suspense>} />

          {/* Gate Pass Module */}
          <Route path="gate-pass" element={<Suspense fallback={<PageLoader />}><GatePassModule /></Suspense>} />
          <Route path="gate-pass/*" element={<Suspense fallback={<PageLoader />}><GatePassModule /></Suspense>} />

          {/* Student Counselling Module */}
          <Route path="student-counselling" element={<Suspense fallback={<PageLoader />}><StudentCounsellingModule /></Suspense>} />
          <Route path="student-counselling/*" element={<Suspense fallback={<PageLoader />}><StudentCounsellingModule /></Suspense>} />

          {/* Student Complaints Module */}
          <Route path="complaints" element={<Suspense fallback={<PageLoader />}><ComplaintsModule /></Suspense>} />
          <Route path="complaints/*" element={<Suspense fallback={<PageLoader />}><ComplaintsModule /></Suspense>} />

          {/* Transport Management Module */}
          <Route path="transport" element={<Suspense fallback={<PageLoader />}><TransportDashboard /></Suspense>} />
          <Route path="transport/routes" element={<Suspense fallback={<PageLoader />}><TRouteList /></Suspense>} />
          <Route path="transport/vehicles" element={<Suspense fallback={<PageLoader />}><TVehicleList /></Suspense>} />
          <Route path="transport/stops" element={<Suspense fallback={<PageLoader />}><TRouteStops /></Suspense>} />
          <Route path="transport/vehicle-routes" element={<Suspense fallback={<PageLoader />}><TVehicleRoutes /></Suspense>} />
          <Route path="transport/student-stops" element={<Suspense fallback={<PageLoader />}><TStudentStops /></Suspense>} />
          <Route path="transport/trips" element={<Suspense fallback={<PageLoader />}><TTripList /></Suspense>} />
          <Route path="transport/driver" element={<Suspense fallback={<PageLoader />}><TDriverTrip /></Suspense>} />
          <Route path="transport/notifications" element={<Suspense fallback={<PageLoader />}><TNotifications /></Suspense>} />
          <Route path="transport/vendors" element={<Suspense fallback={<PageLoader />}><TVendorList /></Suspense>} />
          <Route path="transport/fleet" element={<Suspense fallback={<PageLoader />}><TFleetManagement /></Suspense>} />

          {/* Calendar Module */}
          <Route path="calendar" element={<Suspense fallback={<PageLoader />}><CalendarPage /></Suspense>} />

          {/* Notification Module (Notice Board) */}
          <Route path="notifications" element={<Suspense fallback={<PageLoader />}><NotificationModPage /></Suspense>} />

          {/* Chat Module */}
          <Route path="chat" element={<Suspense fallback={<PageLoader />}><ChatPageComp /></Suspense>} />
          <Route path="chat/settings" element={<Suspense fallback={<PageLoader />}><ChatSettingsComp /></Suspense>} />

          {/* AI Home Tutor */}
          <Route path="ai-tutor/*" element={<Suspense fallback={<PageLoader />}><AiTutorDashboard /></Suspense>} />

          {/* SMS Reports */}
          <Route path="reports/sms" element={<Suspense fallback={<PageLoader />}><SmsReports /></Suspense>} />
          <Route path="reports/academic" element={<Suspense fallback={<PageLoader />}><AcademicReports /></Suspense>} />
          <Route path="reports/study-hours" element={<Suspense fallback={<PageLoader />}><StudyHoursReport /></Suspense>} />
          {/* User Log & Audit Trail — renders same Activity Logs page */}
          <Route path="reports/user-log" element={<Suspense fallback={<PageLoader />}><UserLog /></Suspense>} />
          <Route path="reports/audit" element={<Suspense fallback={<PageLoader />}><ActivityLogs /></Suspense>} />

          {/* Change Password & Profile */}
          <Route path="change-password" element={<Suspense fallback={<PageLoader />}><ChangePassword /></Suspense>} />
          <Route path="profile" element={<ProfileRedirect />} />

          {/* AI Analytics */}
          <Route path="ai-analytics" element={<Suspense fallback={<PageLoader />}><AiAnalyticsPage /></Suspense>} />

          {/* All other module routes — lazy loaded, showing ComingSoon until built */}
          {[
            'video/*',
            'cms',
            'reports/*', 'settings/*',
          ].map(path => (
            <Route key={path} path={path} element={<Suspense fallback={<PageLoader />}><ComingSoon /></Suspense>} />
          ))}
        </Route>

        <Route path="*" element={<Suspense fallback={<div/>}><RootGateway /></Suspense>} />
      </Routes>
    </BrowserRouter>
  );
}
